<template>
    <div class="min-h-screen">
        <!-- Header -->
        <div class="w-full mb-6">
            <div class="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <div class="flex items-center justify-between">
                    <div>
                        <h1 class="text-2xl font-bold text-gray-900">
                            NAWEC Reconciliation Records
                        </h1>
                        <p class="mt-1 text-sm text-gray-500">
                            Transactions in NAWEC system missing locally - with customer return probability analytics
                        </p>
                    </div>
                    <div class="flex items-center space-x-2">
                        <span
                            v-if="statistics"
                            class="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800"
                        >
                            <i class="bx bx-error-circle mr-1"></i>
                            {{ statistics.overview.total_to_recover }} to recover
                        </span>
                        <span
                            v-if="statistics"
                            class="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800"
                        >
                            <i class="bx bx-dollar mr-1"></i>
                            GMD {{ formatPrice(statistics.overview.total_amount) }}
                        </span>
                    </div>
                </div>
            </div>
        </div>

        <!-- Filters -->
        <div class="w-full mx-auto mb-6">
            <div class="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
                <div class="grid grid-cols-1 md:grid-cols-6 gap-4">
                    <div>
                        <label class="block text-xs font-medium text-gray-600 mb-1">
                            Probability
                        </label>
                        <select
                            v-model="filters.probability"
                            class="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-indigo-500 focus:border-indigo-500"
                        >
                            <option value="">All Levels</option>
                            <option value="high">High (≥70%)</option>
                            <option value="medium">Medium (40-69%)</option>
                            <option value="low">Low (&lt;40%)</option>
                            <option value="no_history">No History</option>
                        </select>
                    </div>
                    <div>
                        <label class="block text-xs font-medium text-gray-600 mb-1">
                            Search Meter
                        </label>
                        <input
                            type="text"
                            v-model="filters.search"
                            placeholder="Meter serial..."
                            class="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-indigo-500 focus:border-indigo-500"
                        />
                    </div>
                    <div>
                        <label class="block text-xs font-medium text-gray-600 mb-1">
                            Sort By
                        </label>
                        <select
                            v-model="filters.sort_by"
                            class="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-indigo-500 focus:border-indigo-500"
                        >
                            <option value="return_probability">Return Probability</option>
                            <option value="sale_amount">Sale Amount</option>
                            <option value="total_historical_transactions">Historical Txns</option>
                            <option value="avg_monthly_frequency">Monthly Frequency</option>
                            <option value="request_time">Request Time</option>
                            <option value="meter_serial_no">Meter Serial</option>
                        </select>
                    </div>
                    <div>
                        <label class="block text-xs font-medium text-gray-600 mb-1">
                            Order
                        </label>
                        <select
                            v-model="filters.sort_order"
                            class="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-indigo-500 focus:border-indigo-500"
                        >
                            <option value="desc">Descending</option>
                            <option value="asc">Ascending</option>
                        </select>
                    </div>
                    <div class="flex items-end">
                        <button
                            @click="fetchData(1)"
                            :disabled="loading"
                            class="w-full px-4 py-2 bg-indigo-600 text-white text-sm font-medium rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            <i class="bx bx-search mr-1"></i>
                            <span v-if="!loading">Apply Filters</span>
                            <span v-else>Loading...</span>
                        </button>
                    </div>
                    <div class="flex items-end">
                        <button
                            @click="exportToCSV"
                            :disabled="loading || !records || records.data?.length === 0"
                            class="w-full px-4 py-2 bg-green-600 text-white text-sm font-medium rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            <i class="bx bx-download mr-1"></i> Export CSV
                        </button>
                    </div>
                </div>
            </div>
        </div>

        <!-- Error State -->
        <div v-if="error" class="w-full mx-auto mb-6">
            <div class="bg-red-50 border border-red-200 rounded-lg p-4">
                <div class="flex items-center">
                    <i class="bx bx-error-circle text-red-600 text-xl mr-2"></i>
                    <p class="text-sm text-red-800">{{ error }}</p>
                </div>
            </div>
        </div>

        <!-- Loading State -->
        <div v-if="loading" class="max-w-7xl mx-auto">
            <div class="bg-white rounded-lg shadow-sm border border-gray-200 p-12">
                <div class="flex flex-col items-center justify-center">
                    <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mb-4"></div>
                    <p class="text-gray-600">Loading reconciliation data...</p>
                </div>
            </div>
        </div>

        <!-- Statistics Cards -->
        <div v-if="!loading && statistics" class="w-full mx-auto mb-6">
            <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <!-- Overview Card -->
                <div class="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
                    <div class="flex items-center justify-between">
                        <div class="flex-1">
                            <div class="text-xs font-medium text-gray-500 uppercase mb-1">Total to Recover</div>
                            <div class="text-2xl font-bold text-gray-900">{{ statistics.overview.total_to_recover }}</div>
                            <div class="text-xs text-gray-500 mt-1">{{ statistics.overview.unique_meters }} unique meters</div>
                        </div>
                        <div class="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                            <i class="bx bx-error-circle text-2xl text-red-600"></i>
                        </div>
                    </div>
                </div>

                <!-- Total Amount Card -->
                <div class="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
                    <div class="flex items-center justify-between">
                        <div class="flex-1">
                            <div class="text-xs font-medium text-gray-500 uppercase mb-1">Total Amount</div>
                            <div class="text-2xl font-bold text-green-600">GMD {{ formatPrice(statistics.overview.total_amount) }}</div>
                            <div class="text-xs text-gray-500 mt-1">Avg: GMD {{ formatPrice(statistics.overview.avg_amount) }}</div>
                        </div>
                        <div class="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                            <i class="bx bx-dollar-circle text-2xl text-green-600"></i>
                        </div>
                    </div>
                </div>

                <!-- High Priority Card -->
                <div class="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
                    <div class="flex items-center justify-between">
                        <div class="flex-1">
                            <div class="text-xs font-medium text-gray-500 uppercase mb-1">High Priority</div>
                            <div class="text-2xl font-bold text-indigo-600">{{ statistics.recovery_priority.high_priority_count }}</div>
                            <div class="text-xs text-gray-500 mt-1">GMD {{ formatPrice(statistics.recovery_priority.high_priority_amount) }}</div>
                        </div>
                        <div class="w-12 h-12 bg-indigo-100 rounded-full flex items-center justify-center">
                            <i class="bx bx-target-lock text-2xl text-indigo-600"></i>
                        </div>
                    </div>
                </div>

                <!-- Probability Distribution Card -->
                <div class="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
                    <div class="text-xs font-medium text-gray-500 uppercase mb-2">Probability Distribution</div>
                    <div class="space-y-1">
                        <div class="flex items-center justify-between text-xs">
                            <span class="flex items-center">
                                <span class="w-2 h-2 rounded-full bg-green-500 mr-1"></span>
                                High
                            </span>
                            <span class="font-medium">{{ statistics.probability_distribution.high?.count || 0 }}</span>
                        </div>
                        <div class="flex items-center justify-between text-xs">
                            <span class="flex items-center">
                                <span class="w-2 h-2 rounded-full bg-yellow-500 mr-1"></span>
                                Medium
                            </span>
                            <span class="font-medium">{{ statistics.probability_distribution.medium?.count || 0 }}</span>
                        </div>
                        <div class="flex items-center justify-between text-xs">
                            <span class="flex items-center">
                                <span class="w-2 h-2 rounded-full bg-red-500 mr-1"></span>
                                Low
                            </span>
                            <span class="font-medium">{{ statistics.probability_distribution.low?.count || 0 }}</span>
                        </div>
                        <div class="flex items-center justify-between text-xs">
                            <span class="flex items-center">
                                <span class="w-2 h-2 rounded-full bg-gray-400 mr-1"></span>
                                No History
                            </span>
                            <span class="font-medium">{{ statistics.probability_distribution.no_history?.count || 0 }}</span>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Probability Legend -->
        <div v-if="!loading && records && records.data?.length > 0" class="w-full mx-auto">
            <div class="bg-gray-50 border border-gray-200 rounded-lg p-3 mb-4 flex items-center justify-between">
                <div class="flex items-center space-x-4 text-xs">
                    <span class="font-medium text-gray-700">Legend:</span>
                    <span class="flex items-center">
                        <span class="w-3 h-3 rounded-full bg-green-500 mr-1"></span>
                        High (≥70%)
                    </span>
                    <span class="flex items-center">
                        <span class="w-3 h-3 rounded-full bg-yellow-500 mr-1"></span>
                        Medium (40-69%)
                    </span>
                    <span class="flex items-center">
                        <span class="w-3 h-3 rounded-full bg-red-500 mr-1"></span>
                        Low (&lt;40%)
                    </span>
                    <span class="flex items-center">
                        <span class="w-3 h-3 rounded-full bg-gray-400 mr-1"></span>
                        No History
                    </span>
                </div>
                <div class="text-xs text-gray-500">
                    Showing {{ records.meta.from }}-{{ records.meta.to }} of {{ records.meta.total }} records
                </div>
            </div>

            <!-- Records Table -->
            <div class="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden">
                <div class="px-6 py-4 border-b border-gray-200 bg-gray-50">
                    <div class="flex items-center justify-between">
                        <h3 class="text-sm font-semibold text-gray-700">
                            NAWEC-Only Transactions
                        </h3>
                        <span class="text-xs text-gray-500">
                            Click a row to view details
                        </span>
                    </div>
                </div>

                <div class="overflow-auto max-h-[55vh]">
                    <table class="min-w-full divide-y divide-gray-200">
                        <thead class="bg-gray-50 sticky top-0 z-10">
                            <tr>
                                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">
                                    Request Time
                                </th>
                                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">
                                    Meter / Customer
                                </th>
                                <th class="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">
                                    Sale Amount
                                </th>
                                <th class="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">
                                    Return Probability
                                </th>
                                <th class="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">
                                    Historical Txns
                                </th>
                                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">
                                    Last Transaction
                                </th>
                                <th class="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">
                                    Priority
                                </th>
                            </tr>
                        </thead>
                        <tbody class="bg-white divide-y divide-gray-200">
                            <tr
                                v-for="record in records.data"
                                :key="record.id"
                                class="hover:bg-gray-50 transition-colors cursor-pointer"
                                :class="getRowClass(record.return_probability)"
                                @click="showRecordDetail(record)"
                            >
                                <td class="px-6 py-4 whitespace-nowrap">
                                    <div class="text-sm text-gray-900">
                                        {{ record.request_time | moment('MMM DD, YYYY') }}
                                    </div>
                                    <div class="text-xs text-gray-500">
                                        {{ record.request_time | moment('HH:mm:ss') }}
                                    </div>
                                </td>
                                <td class="px-6 py-4 whitespace-nowrap">
                                    <div class="text-sm font-medium text-gray-900">
                                        {{ record.meter_serial_no }}
                                    </div>
                                    <div v-if="record.customer_name" class="text-xs text-indigo-600 font-medium">
                                        {{ record.customer_name }}
                                    </div>
                                    <div v-else class="text-xs text-gray-500">
                                        {{ record.vendor_name }}
                                    </div>
                                </td>
                                <td class="px-6 py-4 whitespace-nowrap text-right">
                                    <div class="text-sm font-medium text-green-600">
                                        GMD {{ formatPrice(record.sale_amount) }}
                                    </div>
                                    <div class="text-xs text-gray-500">
                                        {{ record.units }} units
                                    </div>
                                </td>
                                <td class="px-6 py-4 whitespace-nowrap text-center">
                                    <span
                                        :class="getProbabilityBadgeClass(record.return_probability)"
                                        class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium"
                                    >
                                        {{ record.return_probability ? parseFloat(record.return_probability).toFixed(1) + '%' : 'N/A' }}
                                    </span>
                                </td>
                                <td class="px-6 py-4 whitespace-nowrap text-right">
                                    <div class="text-sm text-gray-900">
                                        {{ record.total_historical_transactions || 0 }}
                                    </div>
                                    <div class="text-xs text-gray-500">
                                        {{ record.avg_monthly_frequency ? parseFloat(record.avg_monthly_frequency).toFixed(1) + '/mo' : '-' }}
                                    </div>
                                </td>
                                <td class="px-6 py-4 whitespace-nowrap">
                                    <div v-if="record.last_transaction_date" class="text-sm text-gray-900">
                                        {{ record.last_transaction_date | moment('MMM DD, YYYY') }}
                                    </div>
                                    <div v-else class="text-sm text-gray-400">
                                        No history
                                    </div>
                                    <div v-if="record.customer_tenure_months" class="text-xs text-gray-500">
                                        {{ record.customer_tenure_months }} months tenure
                                    </div>
                                </td>
                                <td class="px-6 py-4 whitespace-nowrap text-center">
                                    <span
                                        :class="getPriorityBadgeClass(record.return_probability)"
                                        class="inline-flex items-center px-2 py-1 rounded text-xs font-medium"
                                    >
                                        {{ getPriorityText(record.return_probability) }}
                                    </span>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                <!-- Pagination -->
                <div class="border-t border-gray-200 px-6 py-4 bg-gray-50">
                    <pagination
                        v-if="records"
                        :data="records"
                        :midSize="5"
                        @clicked="fetchData"
                    />
                </div>
            </div>
        </div>

        <!-- Empty State -->
        <div
            v-else-if="!loading && (!records || records.data?.length === 0)"
            class="w-full mx-auto"
        >
            <div class="bg-white rounded-lg shadow-sm border border-gray-200 p-12">
                <div class="text-center">
                    <i class="bx bx-check-circle text-6xl text-green-400 mb-4"></i>
                    <h3 class="text-lg font-medium text-gray-900 mb-2">
                        No Records Found
                    </h3>
                    <p class="text-sm text-gray-500 mb-4">
                        No NAWEC-only transactions match your current filters.
                    </p>
                    <button
                        @click="resetFilters"
                        class="px-4 py-2 bg-indigo-600 text-white text-sm font-medium rounded-md hover:bg-indigo-700"
                    >
                        <i class="bx bx-refresh mr-1"></i>
                        Reset Filters
                    </button>
                </div>
            </div>
        </div>

        <!-- Record Detail Modal -->
        <modal
            name="record-detail-modal"
            :adaptive="true"
            :scrollable="true"
            height="auto"
            :maxWidth="700"
            :clickToClose="true"
        >
            <div v-if="selectedRecord" class="p-6">
                <div class="flex items-center justify-between mb-6">
                    <h2 class="text-xl font-bold text-gray-900">Transaction Details</h2>
                    <button
                        @click="$modal.hide('record-detail-modal')"
                        class="text-gray-400 hover:text-gray-600"
                    >
                        <i class="bx bx-x text-2xl"></i>
                    </button>
                </div>

                <!-- Probability Banner -->
                <div
                    :class="getProbabilityBannerClass(selectedRecord.return_probability)"
                    class="rounded-lg p-4 mb-6"
                >
                    <div class="flex items-center justify-between">
                        <div>
                            <div class="text-sm font-medium opacity-75">Return Probability</div>
                            <div class="text-2xl font-bold">
                                {{ selectedRecord.return_probability ? parseFloat(selectedRecord.return_probability).toFixed(2) + '%' : 'No Data' }}
                            </div>
                        </div>
                        <div class="text-right">
                            <span class="text-sm font-medium px-3 py-1 rounded-full bg-white bg-opacity-20">
                                {{ getPriorityText(selectedRecord.return_probability) }}
                            </span>
                        </div>
                    </div>
                    <p v-if="selectedRecord.probability_explanation" class="text-sm mt-2 opacity-90">
                        {{ selectedRecord.probability_explanation }}
                    </p>
                </div>

                <!-- Transaction Details -->
                <div class="grid grid-cols-2 gap-4 mb-6">
                    <div class="bg-gray-50 rounded-lg p-4">
                        <div class="text-xs text-gray-500 uppercase mb-1">Customer</div>
                        <div class="text-lg font-semibold text-gray-900">{{ selectedRecord.customer_name || 'N/A' }}</div>
                        <div class="text-xs text-gray-500">Meter: {{ selectedRecord.meter_serial_no }}</div>
                    </div>
                    <div class="bg-gray-50 rounded-lg p-4">
                        <div class="text-xs text-gray-500 uppercase mb-1">Sale Amount</div>
                        <div class="text-lg font-semibold text-green-600">GMD {{ formatPrice(selectedRecord.sale_amount) }}</div>
                    </div>
                    <div class="bg-gray-50 rounded-lg p-4">
                        <div class="text-xs text-gray-500 uppercase mb-1">Units</div>
                        <div class="text-lg font-semibold text-gray-900">{{ selectedRecord.units }} kWh</div>
                    </div>
                    <div class="bg-gray-50 rounded-lg p-4">
                        <div class="text-xs text-gray-500 uppercase mb-1">Debt Amount</div>
                        <div class="text-lg font-semibold text-red-600">GMD {{ formatPrice(selectedRecord.debt_amount) }}</div>
                    </div>
                </div>

                <!-- Time Information -->
                <div class="border-t border-gray-200 pt-4 mb-4">
                    <h3 class="text-sm font-semibold text-gray-700 mb-3">Transaction Timeline</h3>
                    <div class="grid grid-cols-2 gap-4 text-sm">
                        <div>
                            <span class="text-gray-500">Request Time:</span>
                            <span class="ml-2 text-gray-900">{{ selectedRecord.request_time | moment('MMM DD, YYYY HH:mm:ss') }}</span>
                        </div>
                        <div>
                            <span class="text-gray-500">Token Time:</span>
                            <span class="ml-2 text-gray-900">{{ selectedRecord.token_time | moment('MMM DD, YYYY HH:mm:ss') }}</span>
                        </div>
                        <div>
                            <span class="text-gray-500">Transaction ID:</span>
                            <span class="ml-2 text-gray-900 font-mono text-xs">{{ selectedRecord.transaction_id }}</span>
                        </div>
                        <div>
                            <span class="text-gray-500">Request ID:</span>
                            <span class="ml-2 text-gray-900 font-mono text-xs">{{ selectedRecord.request_id }}</span>
                        </div>
                    </div>
                </div>

                <!-- Customer Analytics -->
                <div v-if="selectedRecord.total_historical_transactions" class="border-t border-gray-200 pt-4 mb-4">
                    <h3 class="text-sm font-semibold text-gray-700 mb-3">Customer Analytics</h3>
                    <div class="grid grid-cols-2 gap-4 text-sm">
                        <div>
                            <span class="text-gray-500">Historical Transactions:</span>
                            <span class="ml-2 text-gray-900 font-semibold">{{ selectedRecord.total_historical_transactions }}</span>
                        </div>
                        <div>
                            <span class="text-gray-500">Monthly Frequency:</span>
                            <span class="ml-2 text-gray-900 font-semibold">{{ parseFloat(selectedRecord.avg_monthly_frequency).toFixed(2) }}/month</span>
                        </div>
                        <div>
                            <span class="text-gray-500">First Transaction:</span>
                            <span class="ml-2 text-gray-900">{{ selectedRecord.first_transaction_date | moment('MMM DD, YYYY') }}</span>
                        </div>
                        <div>
                            <span class="text-gray-500">Last Transaction:</span>
                            <span class="ml-2 text-gray-900">{{ selectedRecord.last_transaction_date | moment('MMM DD, YYYY') }}</span>
                        </div>
                        <div>
                            <span class="text-gray-500">Customer Tenure:</span>
                            <span class="ml-2 text-gray-900">{{ selectedRecord.customer_tenure_months }} months</span>
                        </div>
                        <div>
                            <span class="text-gray-500">Avg Transaction:</span>
                            <span class="ml-2 text-gray-900">GMD {{ formatPrice(selectedRecord.avg_transaction_amount) }}</span>
                        </div>
                    </div>
                </div>

                <!-- Vendor Info -->
                <div class="border-t border-gray-200 pt-4">
                    <h3 class="text-sm font-semibold text-gray-700 mb-3">Vendor Information</h3>
                    <div class="grid grid-cols-2 gap-4 text-sm">
                        <div>
                            <span class="text-gray-500">Vendor:</span>
                            <span class="ml-2 text-gray-900">{{ selectedRecord.vendor_name }}</span>
                        </div>
                        <div>
                            <span class="text-gray-500">Vendor ID:</span>
                            <span class="ml-2 text-gray-900">{{ selectedRecord.vendor_id }}</span>
                        </div>
                        <div>
                            <span class="text-gray-500">Status:</span>
                            <span class="ml-2 text-gray-900">{{ selectedRecord.transaction_status }}</span>
                        </div>
                        <div>
                            <span class="text-gray-500">Match Status:</span>
                            <span class="ml-2 px-2 py-0.5 bg-red-100 text-red-800 rounded text-xs font-medium">
                                {{ selectedRecord.match_status }}
                            </span>
                        </div>
                    </div>
                </div>

                <!-- Close Button -->
                <div class="mt-6 flex justify-end">
                    <button
                        @click="$modal.hide('record-detail-modal')"
                        class="px-4 py-2 bg-gray-200 text-gray-800 text-sm font-medium rounded-md hover:bg-gray-300"
                    >
                        Close
                    </button>
                </div>
            </div>
        </modal>
    </div>
</template>

<script>
import axios from 'axios';
import { mapGetters } from 'vuex';
import Pagination from '@/components/Pagination';
import FormatPrice from '@/mixins/FormatPrice';
import checkPermissionExist from '@/mixins/checkPermissionExist';

export default {
    name: 'NawecReconciliationRecords',
    middleware: 'auth',
    layout: 'default',

    components: {
        Pagination,
    },

    mixins: [FormatPrice, checkPermissionExist],

    data() {
        return {
            records: null,
            statistics: null,
            loading: false,
            error: null,
            selectedRecord: null,
            filters: {
                probability: '',
                search: '',
                sort_by: 'return_probability',
                sort_order: 'desc',
                per_page: 25,
            },
        };
    },

    computed: {
        ...mapGetters({
            user: 'auth/user',
        }),
    },

    created() {
        // Check permission
        if (!this.checkPermissionExist(this.user.data, 'reconciliations-read')) {
            this.$router.go(-1);
            return;
        }
        this.fetchData(1);
    },

    methods: {
        async fetchData(page = 1) {
            this.loading = true;
            this.error = null;

            try {
                const params = new URLSearchParams({
                    page,
                    per_page: this.filters.per_page,
                    sort_by: this.filters.sort_by,
                    sort_order: this.filters.sort_order,
                });

                if (this.filters.probability) {
                    params.append('probability', this.filters.probability);
                }
                if (this.filters.search) {
                    params.append('search', this.filters.search);
                }

                const response = await axios.get(`/nawec-reconciliation?${params}`);

                if (response.data.success) {
                    // Transform pagination data to match Pagination component's expected format
                    const apiRecords = response.data.data.records;
                    this.records = {
                        data: apiRecords.data,
                        meta: {
                            current_page: apiRecords.current_page,
                            last_page: apiRecords.last_page,
                            total: apiRecords.total,
                            from: apiRecords.from,
                            to: apiRecords.to,
                            per_page: apiRecords.per_page,
                        },
                    };
                    this.statistics = response.data.data.statistics;
                } else {
                    this.error = 'Failed to load reconciliation data.';
                }
            } catch (err) {
                console.error('Error fetching NAWEC reconciliation data:', err);

                if (err.response?.status === 401) {
                    return;
                }

                if (err.response) {
                    switch (err.response.status) {
                        case 404:
                            this.error = 'No reconciliation data found.';
                            break;
                        case 500:
                            this.error = 'Server error. Please try again later.';
                            break;
                        default:
                            this.error = 'An unexpected error occurred.';
                    }
                } else if (err.request) {
                    this.error = 'Network error. Please check your connection.';
                } else {
                    this.error = err.message;
                }
            } finally {
                this.loading = false;
            }
        },

        resetFilters() {
            this.filters = {
                probability: '',
                search: '',
                sort_by: 'return_probability',
                sort_order: 'desc',
                per_page: 25,
            };
            this.fetchData(1);
        },

        showRecordDetail(record) {
            this.selectedRecord = record;
            this.$modal.show('record-detail-modal');
        },

        getRowClass(probability) {
            const prob = parseFloat(probability);
            if (isNaN(prob) || prob === 0) return 'bg-gray-50';
            if (prob >= 70) return 'bg-green-50';
            if (prob >= 40) return 'bg-yellow-50';
            return 'bg-red-50';
        },

        getProbabilityBadgeClass(probability) {
            const prob = parseFloat(probability);
            if (isNaN(prob) || prob === 0) return 'bg-gray-100 text-gray-800';
            if (prob >= 70) return 'bg-green-100 text-green-800';
            if (prob >= 40) return 'bg-yellow-100 text-yellow-800';
            return 'bg-red-100 text-red-800';
        },

        getPriorityBadgeClass(probability) {
            const prob = parseFloat(probability);
            if (isNaN(prob) || prob === 0) return 'bg-gray-200 text-gray-700';
            if (prob >= 70) return 'bg-green-600 text-white';
            if (prob >= 40) return 'bg-yellow-500 text-white';
            return 'bg-red-500 text-white';
        },

        getPriorityText(probability) {
            const prob = parseFloat(probability);
            if (isNaN(prob) || prob === 0) return 'No History';
            if (prob >= 80) return 'Very High';
            if (prob >= 70) return 'High';
            if (prob >= 40) return 'Medium';
            if (prob >= 20) return 'Low';
            return 'Very Low';
        },

        getProbabilityBannerClass(probability) {
            const prob = parseFloat(probability);
            if (isNaN(prob) || prob === 0) return 'bg-gray-500 text-white';
            if (prob >= 70) return 'bg-green-600 text-white';
            if (prob >= 40) return 'bg-yellow-500 text-white';
            return 'bg-red-500 text-white';
        },

        async exportToCSV() {
            if (!this.records || !this.records.data) return;

            // Show loading
            this.loading = true;

            try {
                // Fetch all records with current filters for export
                let allRecords = [];
                let page = 1;
                let hasMore = true;

                while (hasMore) {
                    const params = new URLSearchParams({
                        page,
                        per_page: 100,
                        sort_by: this.filters.sort_by,
                        sort_order: this.filters.sort_order,
                    });

                    if (this.filters.probability) {
                        params.append('probability', this.filters.probability);
                    }
                    if (this.filters.search) {
                        params.append('search', this.filters.search);
                    }

                    const response = await axios.get(`/nawec-reconciliation?${params}`);

                    if (response.data.success) {
                        allRecords = [...allRecords, ...response.data.data.records.data];
                        hasMore = response.data.data.records.next_page_url !== null;
                        page++;
                    } else {
                        hasMore = false;
                    }

                    // Safety limit
                    if (page > 50) {
                        hasMore = false;
                    }
                }

                // Generate CSV
                const headers = [
                    'Request Time',
                    'Transaction ID',
                    'Meter Serial',
                    'Customer Name',
                    'Vendor',
                    'Sale Amount',
                    'Debt Amount',
                    'Units',
                    'Return Probability',
                    'Priority',
                    'Historical Transactions',
                    'Monthly Frequency',
                    'Customer Tenure (months)',
                    'Last Transaction',
                    'Probability Explanation',
                ];

                const rows = allRecords.map(record => {
                    return [
                        record.request_time,
                        record.transaction_id,
                        record.meter_serial_no,
                        `"${(record.customer_name || '').replace(/"/g, '""')}"`,
                        `"${record.vendor_name}"`,
                        record.sale_amount,
                        record.debt_amount,
                        record.units,
                        record.return_probability || '0',
                        this.getPriorityText(record.return_probability),
                        record.total_historical_transactions || 0,
                        record.avg_monthly_frequency || 0,
                        record.customer_tenure_months || 0,
                        record.last_transaction_date || '',
                        `"${(record.probability_explanation || '').replace(/"/g, '""')}"`,
                    ];
                });

                const csvContent = [
                    headers.join(','),
                    ...rows.map(row => row.join(',')),
                ].join('\n');

                const blob = new Blob([csvContent], {
                    type: 'text/csv;charset=utf-8;',
                });
                const url = window.URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.download = `nawec_reconciliation_${new Date().toISOString().split('T')[0]}.csv`;
                link.click();
                window.URL.revokeObjectURL(url);
            } catch (err) {
                console.error('Error exporting CSV:', err);
                this.error = 'Failed to export data. Please try again.';
            } finally {
                this.loading = false;
            }
        },
    },
};
</script>
